import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { MultiSelect } from '../components/MultiSelect';
import './AdvertiserForm.css';

interface Offer {
  id: number;
  name: string;
  advertiser: string;
  advertiserId: number;
  goalType: 'install' | 'lead' | 'sale' | 'event';
  payout: number;
  payoutCurrency: string;
  isActive: boolean;
  isTest: boolean;
  dailyCap?: number;
  redirectUrl: string;
  landingPageUrl: string;
  createdAt: string;
  updatedAt: string;
  previewUrl?: string;
  description?: string;
  restriction?: string;
  conversionRate: number;
  totalClicks: number;
  totalConversions: number;
  countries: string[];
  deviceTypes: string[];
}

const ViewOffer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [allPublishers, setAllPublishers] = useState<any[]>([]);
  const [selectedPublisherForLink, setSelectedPublisherForLink] = useState<string>('');
  const [advertiserApprovals, setAdvertiserApprovals] = useState<any[]>([]);
  const [customPayoutPublisherIds, setCustomPayoutPublisherIds] = useState<(number | string)[]>([]);
  const [customPayoutValue, setCustomPayoutValue] = useState<string>('');
  const [savingCustomPayout, setSavingCustomPayout] = useState(false);
  const [customPayoutMsg, setCustomPayoutMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [offerResponse, assignmentsResponse, publishersResponse] = await Promise.all([
          apiService.getOffer(parseInt(id)),
          apiService.getOfferPublishers(parseInt(id)),
          apiService.getPublishers()
        ]);
        setOffer(offerResponse.offer);
        setAssignments(assignmentsResponse.assignments || []);
        setAllPublishers(publishersResponse.publishers || []);

        // Fetch advertiser-level approvals
        if (offerResponse.offer?.advertiser_id) {
          try {
            const approvals = await apiService.getAdvertiserPublishers(offerResponse.offer.advertiser_id);
            setAdvertiserApprovals(approvals.publishers || []);
          } catch (err) {
            setAdvertiserApprovals([]);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch offer');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleEdit = () => {
    navigate(`/offers/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/offers');
  };

  const handleCopyOffer = () => {
    // Navigate to add offer page with current offer data
    navigate('/offers/add', {
      state: {
        copyFromOffer: {
          name: offer.name + ' (Copy)',
          advertiser_id: offer.advertiser_id,
          goal_type: offer.goal_type,
          event_name: offer.event_name,
          category: offer.category,
          payout: offer.payout,
          payout_currency: offer.payout_currency,
          daily_click_cap: offer.daily_click_cap,
          monthly_click_cap: offer.monthly_click_cap,
          daily_conversion_cap: offer.daily_conversion_cap,
          monthly_conversion_cap: offer.monthly_conversion_cap,
          start_date: offer.start_date,
          end_date: offer.end_date,
          redirect_url_template: offer.redirect_url_template,
          preview_url: offer.preview_url,
          description: offer.description,
          restriction: offer.restriction,
          is_active: false, // Set to inactive by default for safety
          is_test: offer.is_test,
          countries: offer.countries || [],
          os: offer.os || []
        }
      }
    });
  };

  const handleRefresh = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [offerResponse, assignmentsResponse] = await Promise.all([
        apiService.getOffer(parseInt(id)),
        apiService.getOfferPublishers(parseInt(id))
      ]);
      setOffer(offerResponse.offer);
      setAssignments(assignmentsResponse.assignments || []);

      // Refresh advertiser-level approvals
      if (offerResponse.offer?.advertiser_id) {
        try {
          const approvals = await apiService.getAdvertiserPublishers(offerResponse.offer.advertiser_id);
          setAdvertiserApprovals(approvals.publishers || []);
        } catch (err) {
          setAdvertiserApprovals([]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to refresh offer data');
    } finally {
      setLoading(false);
    }
  };

  // Get publishers that are approved at BOTH advertiser and offer level
  const getDoubleApprovedPublishers = () => {
    // Get publisher IDs approved at advertiser level
    // API returns publishers with approval_status field
    const advertiserApprovedIds = new Set(
      advertiserApprovals
        .filter((approval: any) => approval.approval_status === 'approved')
        .map((approval: any) => approval.id)
    );

    // Filter assignments to only show publishers approved at both levels
    return assignments.filter((assignment: any) => {
      const isOfferApproved = assignment.status === 'approved';
      const isAdvertiserApproved = advertiserApprovedIds.has(assignment.publisher.id);

      return isOfferApproved && isAdvertiserApproved;
    });
  };

  if (loading) {
    return (
      <div className="advertiser-view-page">
        <div className="loading">Loading offer details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Error: {error}</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Offers
        </button>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="advertiser-view-page">
        <div className="error">Offer not found</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          Back to Offers
        </button>
      </div>
    );
  }

  const handleSaveCustomPayout = async () => {
    if (customPayoutPublisherIds.length === 0) return;
    const selected = assignments.filter((a: any) => customPayoutPublisherIds.includes(a.publisher.id));
    if (selected.length === 0) return;
    setSavingCustomPayout(true);
    setCustomPayoutMsg(null);
    try {
      await Promise.all(selected.map((a: any) =>
        apiService.updateOfferPublisherAssignment(a.id.toString(), {
          custom_payout: customPayoutValue || ''
        })
      ));
      setCustomPayoutMsg({ type: 'success', text: `Custom payout saved for ${selected.length} affiliate(s).` });
      setCustomPayoutPublisherIds([]);
      setCustomPayoutValue('');
      const res = await apiService.getOfferPublishers(parseInt(id!));
      setAssignments(res.assignments || []);
    } catch (err: any) {
      setCustomPayoutMsg({ type: 'error', text: err.message || 'Failed to save.' });
    } finally {
      setSavingCustomPayout(false);
    }
  };

  const handleRemoveCustomPayout = async (assignmentId: string, publisherName: string) => {
    if (!window.confirm(`Remove custom payout for ${publisherName}?`)) return;
    try {
      await apiService.updateOfferPublisherAssignment(assignmentId, { custom_payout: '' });
      const res = await apiService.getOfferPublishers(parseInt(id!));
      setAssignments(res.assignments || []);
    } catch (err: any) {
      alert('Failed to remove custom payout.');
    }
  };

  const getStatusDisplay = (offer: any) => {
    if (offer.is_test) return { text: 'Test', class: 'test' };
    if (offer.is_active) return { text: 'Active', class: 'active' };
    return { text: 'Inactive', class: 'inactive' };
  };

  const status = getStatusDisplay(offer);
  const offerEvents = Array.isArray(offer.events) ? offer.events : [];
  const primaryEvent = offerEvents.find((event: any) => event.is_primary) || {
    event_name: offer.goal_type || 'install',
    is_primary: true,
    is_payable: true,
    payout: offer.payout,
    revenue: offer.payout,
    payout_currency: offer.payout_currency,
    daily_conversion_cap: offer.daily_conversion_cap,
    monthly_conversion_cap: offer.monthly_conversion_cap,
    publisher_postback_enabled: true,
    postback_status: 'start'
  };
  const secondaryEvents = offerEvents.filter((event: any) => !event.is_primary);
  const primaryEventName = primaryEvent.event_name || offer.goal_type || 'install';
  const primaryEndpoint = primaryEventName === 'install'
    ? 'https://track.adbizzmedia.com/gpixel?click_id={click_id}'
    : `https://track.adbizzmedia.com/goal?click_id={click_id}&event=${encodeURIComponent(primaryEventName)}`;

  const formatCap = (value: any) => (
    value !== null && value !== undefined && value !== '' ? Number(value).toLocaleString() : 'Unlimited'
  );

  const formatCurrency = (value: any, currency = offer.payout_currency || 'USD') => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 'N/A';
    return `${currency} ${parsed.toFixed(2)}`;
  };

  const getEventEndpoint = (event: any) => (
    event.event_name === 'install'
      ? 'https://track.adbizzmedia.com/gpixel?click_id={click_id}'
      : `https://track.adbizzmedia.com/goal?click_id={click_id}&event=${encodeURIComponent(event.event_name || '')}`
  );

  const renderEventDetailGrid = (event: any, options: { primary?: boolean } = {}) => {
    const currency = event.payout_currency || offer.payout_currency || 'USD';
    const isPrimary = options.primary === true;

    return (
      <div className="offer-event-detail-grid">
        <div className="offer-event-detail-item">
          <span>Goal Type</span>
          <strong>{isPrimary ? (offer.goal_type || event.event_name || 'N/A') : 'Supporting Event'}</strong>
        </div>
        <div className="offer-event-detail-item">
          <span>Payable</span>
          <strong>{isPrimary || event.is_payable ? 'Yes' : 'No'}</strong>
        </div>
        {isPrimary && (
          <div className="offer-event-detail-item">
            <span>Payout</span>
            <strong>{formatCurrency(event.payout ?? offer.payout, currency)}</strong>
          </div>
        )}
        <div className="offer-event-detail-item">
          <span>Daily Conversion Cap</span>
          <strong>{formatCap(isPrimary ? (event.daily_conversion_cap ?? offer.daily_conversion_cap) : event.daily_conversion_cap)}</strong>
        </div>
        <div className="offer-event-detail-item">
          <span>Monthly Conversion Cap</span>
          <strong>{formatCap(isPrimary ? (event.monthly_conversion_cap ?? offer.monthly_conversion_cap) : event.monthly_conversion_cap)}</strong>
        </div>
      </div>
    );
  };

  return (
    <div className="advertiser-view-page">
      <div className="view-header">
        <h1>Offer Details</h1>
        <div className="header-actions">
          <button className="btn btn-info" onClick={handleRefresh}>
            🔄 Refresh
          </button>
          <button className="btn btn-success" onClick={handleCopyOffer}>
            📋 Copy Offer
          </button>
          <button className="btn btn-primary" onClick={handleEdit}>
            Edit
          </button>
          <button className="btn btn-warning" onClick={handleBack}>
            Back
          </button>
        </div>
      </div>

      <div className="view-form-container">
        <table className="advertiser-details-table">
          <tbody>
            <tr>
              <td className="form-label">ID</td>
              <td className="form-value">{offer.id}</td>
            </tr>

            <tr>
              <td className="form-label">Name</td>
              <td className="form-value">{offer.name}</td>
            </tr>

            <tr>
              <td className="form-label">Advertiser</td>
              <td className="form-value">{offer.advertiser?.name || 'N/A'}</td>
            </tr>

            <tr>
              <td className="form-label">Goal Type</td>
              <td className="form-value">
                <span className={`status-badge ${offer.goal_type}`}>
                  {offer.goal_type?.toUpperCase()}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Payout</td>
              <td className="form-value">
                <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#27ae60' }}>
                  ${offer.payout} {offer.payout_currency}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Publisher Payouts by Tier</td>
              <td className="form-value">
                <div className="tier-payouts-display">
                  <div className="tier-payout-item">
                    <span className="tier-label">Standard Tier (40%):</span>
                    <span className="tier-amount">{offer.payout_currency} {(parseFloat(offer.payout) * 0.40).toFixed(2)}</span>
                  </div>
                  <div className="tier-payout-item">
                    <span className="tier-label">Premium Tier (60%):</span>
                    <span className="tier-amount">{offer.payout_currency} {(parseFloat(offer.payout) * 0.60).toFixed(2)}</span>
                  </div>
                  <div className="tier-payout-item">
                    <span className="tier-label">Sensitive Tier (80%):</span>
                    <span className="tier-amount">{offer.payout_currency} {(parseFloat(offer.payout) * 0.80).toFixed(2)}</span>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '10px' }}>Traffic Caps</td>
              <td className="form-value">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ padding: '12px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>Daily Click Cap</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      {offer.daily_click_cap != null ? offer.daily_click_cap.toLocaleString() : 'Unlimited'}
                    </div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>Monthly Click Cap</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      {offer.monthly_click_cap != null ? offer.monthly_click_cap.toLocaleString() : 'Unlimited'}
                    </div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>Daily Conversion Cap</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      {offer.daily_conversion_cap != null ? offer.daily_conversion_cap.toLocaleString() : 'Unlimited'}
                    </div>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>Monthly Conversion Cap</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      {offer.monthly_conversion_cap != null ? offer.monthly_conversion_cap.toLocaleString() : 'Unlimited'}
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Primary Event</td>
              <td className="form-value">
                <div className="offer-event-section">
                  <div className="offer-event-section-header">
                    <div>
                      <h3>{primaryEventName}</h3>
                    </div>
                    <span className="status-badge active">Payable</span>
                  </div>

                  {renderEventDetailGrid(primaryEvent, { primary: true })}

                  <div className="offer-event-endpoint">
                    <span>Postback Endpoint</span>
                    <code>{primaryEndpoint}</code>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Secondary Events</td>
              <td className="form-value">
                {secondaryEvents.length > 0 ? (
                  <div className="offer-event-list">
                    {secondaryEvents.map((event: any) => (
                      <div className="offer-event-section secondary" key={event.id || event.event_name}>
                        <div className="offer-event-section-header">
                          <div>
                            <h3>{event.event_name || 'Unnamed Event'}</h3>
                          </div>
                          <span className="status-badge inactive">Non-payable</span>
                        </div>

                        {renderEventDetailGrid(event)}

                        <div className="offer-event-endpoint">
                          <span>Postback Endpoint</span>
                          <code>{getEventEndpoint(event)}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="offer-event-empty">
                    No secondary events configured for this offer.
                  </div>
                )}
              </td>
            </tr>

            <tr>
              <td className="form-label">Target Countries</td>
              <td className="form-value">
                {offer.countries && offer.countries.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {offer.countries.map((country: string, index: number) => (
                      <span
                        key={index}
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {country}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#a0aec0' }}>All Countries</span>
                )}
              </td>
            </tr>

            <tr>
              <td className="form-label">Target Devices/OS</td>
              <td className="form-value">
                {offer.os && offer.os.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {offer.os.map((device: string, index: number) => (
                      <span
                        key={index}
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: '#9b59b6',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {device}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#a0aec0' }}>All Devices</span>
                )}
              </td>
            </tr>

            <tr>
              <td className="form-label">Start Date</td>
              <td className="form-value">
                {offer.start_date
                  ? new Date(offer.start_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'Asia/Kolkata'
                    }) + ' (IST)'
                  : 'No start date'}
              </td>
            </tr>

            <tr>
              <td className="form-label">End Date</td>
              <td className="form-value">
                {offer.end_date
                  ? new Date(offer.end_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'Asia/Kolkata'
                    }) + ' (IST)'
                  : 'No end date'}
              </td>
            </tr>

            <tr>
              <td className="form-label">Status</td>
              <td className="form-value">
                <span className={`status-badge ${status.class}`}>
                  {status.text}
                </span>
              </td>
            </tr>

            <tr>
              <td className="form-label">Created</td>
              <td className="form-value">{new Date(offer.created_at).toLocaleDateString()}</td>
            </tr>

            <tr>
              <td className="form-label">Updated</td>
              <td className="form-value">{new Date(offer.updated_at).toLocaleDateString()}</td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Publisher Tracking Link Generator</td>
              <td className="form-value">
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                    Select Publisher:
                  </label>
                  <select
                    value={selectedPublisherForLink}
                    onChange={(e) => setSelectedPublisherForLink(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#4a5568',
                      color: '#ecf0f1',
                      border: '1px solid #718096',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Select an assigned publisher --</option>
                    {getDoubleApprovedPublishers().map((assignment: any) => (
                      <option key={assignment.publisher.id} value={assignment.publisher.id}>
                        {assignment.publisher.name} (ID: {assignment.publisher.id})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPublisherForLink && (
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                        Tracking Link for Selected Publisher:
                      </label>
                      <code style={{
                        fontSize: '12px',
                        backgroundColor: '#27ae60',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        wordBreak: 'break-all',
                        display: 'block',
                        color: '#fff'
                      }}>
                        https://track.adbizzmedia.com/tracking?aff_id={selectedPublisherForLink}&offer_id={offer.id}&aff_sub1={'{click_id}'}&aff_sub2={'{affiliate_id}'}&gaid={'{google_id}'}&ifaid={'{ifa_id}'}&device_id={'{device_id}'}&aff_sub3={'{app_name}'}
                      </code>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        const publisherLink = `https://track.adbizzmedia.com/tracking?aff_id=${selectedPublisherForLink}&offer_id=${offer.id}&aff_sub1={click_id}&aff_sub2={affiliate_id}&gaid={google_id}&ifaid={ifa_id}&device_id={device_id}&aff_sub3={app_name}`;
                        navigator.clipboard.writeText(publisherLink);
                        alert('Publisher tracking link copied to clipboard!');
                      }}
                      style={{ marginRight: '10px' }}
                    >
                      📋 Copy Link
                    </button>
                  </div>
                )}

                {!selectedPublisherForLink && getDoubleApprovedPublishers().length === 0 && (
                  <div style={{ padding: '15px', textAlign: 'center', color: '#e74c3c', backgroundColor: '#4a5568', borderRadius: '4px', fontSize: '13px' }}>
                    ⚠️ No publishers are approved for both this advertiser and this offer.
                    <br />
                    Publishers must be approved at both the advertiser level and offer level to appear here.
                  </div>
                )}
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Custom Payouts</td>
              <td className="form-value">
                {/* Set custom payout form */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <MultiSelect
                      options={assignments.map((a: any) => ({ id: a.publisher.id, name: `${a.publisher.name} (ID: ${a.publisher.id})` }))}
                      value={customPayoutPublisherIds}
                      onChange={setCustomPayoutPublisherIds}
                      placeholder="-- Select Affiliates --"
                    />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Custom payout ($)"
                    value={customPayoutValue}
                    onChange={(e) => setCustomPayoutValue(e.target.value)}
                    style={{ width: '160px', padding: '8px', backgroundColor: '#4a5568', color: '#ecf0f1', border: '1px solid #718096', borderRadius: '4px', fontSize: '14px' }}
                  />
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleSaveCustomPayout}
                    disabled={customPayoutPublisherIds.length === 0 || savingCustomPayout}
                  >
                    {savingCustomPayout ? 'Saving...' : 'Save'}
                  </button>
                </div>

                {customPayoutMsg && (
                  <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '4px', fontSize: '13px', backgroundColor: customPayoutMsg.type === 'success' ? '#27ae60' : '#e74c3c', color: '#fff' }}>
                    {customPayoutMsg.text}
                  </div>
                )}

                {/* List of affiliates with custom payouts */}
                {assignments.filter((a: any) => a.custom_payout).length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#4a5568' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '13px' }}>Affiliate</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '13px' }}>Default Payout</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '13px' }}>Custom Payout</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '13px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.filter((a: any) => a.custom_payout).map((a: any) => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #4a5568' }}>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ fontWeight: 'bold' }}>{a.publisher.name}</div>
                            <div style={{ fontSize: '12px', color: '#a0aec0' }}>ID: {a.publisher.id}</div>
                          </td>
                          <td style={{ padding: '8px 12px', color: '#a0aec0' }}>${parseFloat(offer.payout).toFixed(2)}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ fontWeight: 'bold', color: '#27ae60' }}>${parseFloat(a.custom_payout).toFixed(2)}</span>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveCustomPayout(a.id.toString(), a.publisher.name)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '12px', color: '#a0aec0', fontSize: '13px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    No custom payouts set for this offer.
                  </div>
                )}
              </td>
            </tr>

            <tr>
              <td className="form-label">Stored Redirect URL</td>
              <td className="form-value">
                <code style={{ fontSize: '12px', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px', wordBreak: 'break-all' }}>
                  {offer.redirect_url_template || 'Not configured'}
                </code>
              </td>
            </tr>

            <tr>
              <td className="form-label">Preview URL</td>
              <td className="form-value">
                {offer.preview_url ? (
                  <a href={offer.preview_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db', textDecoration: 'underline' }}>
                    {offer.preview_url}
                  </a>
                ) : (
                  <span style={{ color: '#95a5a6' }}>Not configured</span>
                )}
              </td>
            </tr>

            {offer.description && (
              <tr>
                <td className="form-label">Description</td>
                <td className="form-value">
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    {offer.description}
                  </div>
                </td>
              </tr>
            )}

            {offer.restriction && (
              <tr>
                <td className="form-label">Restrictions</td>
                <td className="form-value">
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#e74c3c' }}>
                    {offer.restriction}
                  </div>
                </td>
              </tr>
            )}

            <tr>
              <td className="form-label">Slug</td>
              <td className="form-value">
                <code style={{ fontSize: '12px', backgroundColor: '#4a5568', padding: '4px 8px', borderRadius: '3px' }}>
                  {offer.slug}
                </code>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Performance Stats</td>
              <td className="form-value">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', padding: '10px' }}>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{offer.total_clicks || 0}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Total Clicks</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{offer.total_conversions || 0}</div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Conversions</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      {offer.total_clicks > 0 ? ((offer.total_conversions || 0) / offer.total_clicks * 100).toFixed(1) : 0}%
                    </div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>CVR</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>
                      ${((offer.total_conversions || 0) * parseFloat(offer.payout || 0)).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>Total Payout</div>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td className="form-label" style={{ verticalAlign: 'top', paddingTop: '20px' }}>Assigned Publishers</td>
              <td className="form-value">
                <div style={{ marginBottom: '15px' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleEdit}
                  >
                    Manage Publishers (Edit Page)
                  </button>
                </div>

                {assignments.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                    No publishers assigned to this offer yet.
                  </div>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#4a5568', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Publisher</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Custom Payout</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Assigned Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map((assignment: any) => (
                          <tr key={assignment.id} style={{ borderBottom: '1px solid #4a5568' }}>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontWeight: 'bold' }}>{assignment.publisher.name}</div>
                              <div style={{ fontSize: '12px', color: '#a0aec0' }}>{assignment.publisher.email}</div>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span className={`status-badge ${assignment.status}`}>
                                {assignment.status.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              {assignment.custom_payout ? (
                                <span style={{ fontWeight: 'bold', color: '#27ae60' }}>
                                  ${assignment.custom_payout}
                                </span>
                              ) : (
                                <span style={{ color: '#a0aec0' }}>Default (${offer.payout})</span>
                              )}
                            </td>
                            <td style={{ padding: '10px', fontSize: '13px' }}>
                              {new Date(assignment.assigned_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#a0aec0' }}>
                      To add or remove publishers, use the Edit page.
                    </div>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default ViewOffer;
